import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { loadConfig, loadOrders, loadJob } from "src/redux/actions/woocommerce";
import Alert from "src/components/Alert";
import SyncStatus from "src/components/SyncStatus";
import getSymbolFromCurrency from "currency-symbol-map";
import { migrateWoocommerceData, migrateWoocommerceDataForOrders } from "src/api";
import { addToast } from "src/redux/actions/toasts";
import ServerPaginationTable from "src/components/ServerPaginationTable";

function Orders() {
  const orders = useSelector((state) => state.woocommerce.orders);
  const dispatch = useDispatch();
  const job = useSelector((state) => state.woocommerce.job);
  const [pageCount, setPageCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [status, setStatus] = useState([]);
  const [sortBy, setSortBy] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(loadOrders());
    dispatch(loadConfig());
  }, []);

  useEffect(() => {
    dispatch(loadJob());
  }, job);

  const data = useMemo(() => orders.data, [orders]);

  const columns = useMemo(
    () => [
      {
        Header: "Deskera ID",
        accessor: "deskeraDocumentCode",
      },
      {
        Header: "Order ID",
        accessor: "cartInvoiceId",
      },
      {
        Header: "Customer Name",
        accessor: "customerName",
      },
      {
        Header: "Customer Email",
        accessor: "customerEmail",
      },
      {
        Header: "Total Amount",
        accessor: "totalAmount",
        Cell: ({ cell: { row } }) => (
          <span>
            {row.original.totalAmount && getSymbolFromCurrency(row.original.currency)}{" "}
            {row.original.totalAmount}
          </span>
        ),
        className: "text-right",
      },
      {
        Header: "Sync Status",
        accessor: "syncStatus",
        Cell: ({ cell: { row } }) => {
          return (
            <SyncStatus
              status={row.original.syncStatus}
              reason={row.original.reason}
              id={row.original.cartInvoiceId}
            />
          );
        },
      },
      {
        Header: "Sync Remarks",
        accessor: "reason",
        disableSortBy: true,
        Cell: ({ cell: { row } }) => {
          return <>{!(row.original.syncStatus === "SUCCESSFUL") && row.original.reason}</>;
        },
      }
    ],
    []
  );

  const config = useSelector((state) => state.woocommerce.config);

  useEffect(() => {
    setLoading(false);
    if (data && data.totalPages) {
      setPageCount(data.totalPages);
    }
  }, [data]);

  const refreshData = ({ pageSize, pageIndex, status, sortBy, search }) => {
    setPageSize(pageSize);
    setPageIndex(pageIndex);
    setStatus(status);
    setSortBy(sortBy);
    setLoading(true);
    dispatch(loadOrders(pageSize, pageIndex, status, sortBy, search ?? ""));
  };

  async function handleSyncDataSubmit() {
    try {
      const resp = await migrateWoocommerceData();
    } catch (err) {
      dispatch(
        addToast({
          type: "danger",
          title: "Failure",
          message: err.message,
        })
      );
    }
  }

  return (
    <div className="p-4">
      <h3 className="mb-3">Orders</h3>
      {(!config.data || !config.data.syncEnabled) && (
        <Alert type="warning">
          Your synced orders will appear here. Please complete{" "}
          <Link to="/app/woocommerce" className="text-alert-warning">
            <u>Setup</u>
          </Link>{" "}
          before continuing.
        </Alert>
      )}
      <ServerPaginationTable
        data={data.content ? data.content : []}
        columns={columns}
        migrate={migrateWoocommerceDataForOrders}
        cart="woocommerce"
        refresh={refreshData}
        tab="Orders"
        pageCount={pageCount}
        loading={loading}
        rowId="cartInvoiceId"
      />
    </div>
  );
}

export default Orders;
